import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { StripeMode, UpdateStripeSettingsInput } from "@eaub/shared";
import { useApi } from "../api/ApiProvider";
import { useConfig } from "../config/ConfigProvider";
import { useAsync } from "../lib/useAsync";
import { Spinner } from "../components/Spinner";
import { ErrorBanner } from "../components/ErrorBanner";

/** Yes/no chip for whether a credential is currently stored. */
function SetBadge({ set }: { set: boolean }) {
  return set ? (
    <span className="badge badge--success">Set</span>
  ) : (
    <span className="badge badge--muted">Not set</span>
  );
}

const MODE_LABEL: Record<StripeMode, string> = {
  live: "Live",
  test: "Test",
};

export function StripeIntegration() {
  const api = useApi();
  const config = useConfig();

  const { data, loading, error, reload, setData } = useAsync(
    () => api.getStripeSettings(),
    [api],
  );

  // The webhook URL to paste into Stripe's Developer Dashboard endpoint.
  const webhookUrl = `${config.apiBaseUrl.replace(/\/+$/, "")}/integrations/stripe/webhook`;

  const [secretKey, setSecretKey] = useState("");
  const [signingSecret, setSigningSecret] = useState("");
  const [skuMetadataKey, setSkuMetadataKey] = useState("");
  // Which mode's credentials this form is editing (not the active one — that is
  // chosen by the site-wide dev-mode toggle on the Integrations hub).
  const [mode, setMode] = useState<StripeMode>("live");

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | undefined>(undefined);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  // Prefill the non-secret fields from the loaded status (secrets stay blank).
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (hydrated || !data) return;
    setSkuMetadataKey(data.skuMetadataKey);
    setHydrated(true);
  }, [data, hydrated]);

  // Switching which mode we edit clears the write-only secret inputs so they
  // can't leak into the other set.
  const changeMode = (next: StripeMode) => {
    setMode(next);
    setSecretKey("");
    setSigningSecret("");
    setSaved(false);
  };

  const copyWebhookUrl = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard may be unavailable (e.g. insecure context); ignore silently.
    }
  };

  const save = async () => {
    setSaveError(undefined);
    setSaved(false);

    // Send only what the admin provided; secrets are write-only and skipped when blank.
    const input: UpdateStripeSettingsInput = { mode };
    if (secretKey.trim()) input.secretKey = secretKey.trim();
    if (signingSecret.trim()) input.webhookSigningSecret = signingSecret.trim();
    if (skuMetadataKey.trim()) input.skuMetadataKey = skuMetadataKey.trim();

    setSaving(true);
    try {
      const next = await api.updateStripeSettings(input);
      setData(() => next);
      setSecretKey("");
      setSigningSecret("");
      setSaved(true);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spinner label="Loading…" />;
  if (error) return <ErrorBanner message={error.message} onRetry={reload} />;

  const status = data;
  const anyConfigured = Boolean(status?.test.configured || status?.live.configured);
  const modeStatus = mode === "live" ? status?.live : status?.test;

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Stripe integration</h1>
          <p className="page__subtitle page__subtitle--badges">
            {anyConfigured ? (
              <span className="badge badge--success">Connected</span>
            ) : (
              <span className="badge badge--muted">Not configured</span>
            )}
            <span className="muted">
              Paid Checkout sales decrement our stock; products are deactivated on Stripe at zero and
              reactivated on restock.
            </span>
          </p>
        </div>
      </div>

      <p className="form-hint muted">
        The active mode is set site-wide by the{" "}
        <Link to="/settings/integrations">development-mode toggle</Link>. Store credentials for both
        modes here so flipping the switch never requires re-entering them.
      </p>

      {saveError ? <ErrorBanner title="Save failed" message={saveError} /> : null}

      <section className="panel">
        <div className="panel__header">
          <h2 className="panel__title">Status</h2>
        </div>
        <dl className="detail-grid">
          {(["live", "test"] as const).map((m) => {
            const s = status?.[m];
            return (
              <div key={m}>
                <dt className="muted">
                  {MODE_LABEL[m]} {s?.configured ? <SetBadge set /> : <SetBadge set={false} />}
                </dt>
                <dd>
                  <div>
                    Secret key: <SetBadge set={Boolean(s?.hasSecretKey)} />
                  </div>
                  <div>
                    Signing secret: <SetBadge set={Boolean(s?.hasWebhookSigningSecret)} />
                  </div>
                </dd>
              </div>
            );
          })}
          <div>
            <dt className="muted">SKU metadata key</dt>
            <dd>
              <code>{status?.skuMetadataKey}</code>
            </dd>
          </div>
          <div>
            <dt className="muted">Last updated</dt>
            <dd>
              {status?.updatedAt ? (
                <>
                  {new Date(status.updatedAt).toLocaleString()}
                  {status.updatedBy ? ` by ${status.updatedBy}` : ""}
                </>
              ) : (
                <span className="muted">Never</span>
              )}
            </dd>
          </div>
        </dl>
      </section>

      <section className="panel">
        <div className="panel__header">
          <h2 className="panel__title">Webhook URL</h2>
        </div>
        <p className="muted">
          In the Stripe Dashboard, add a webhook endpoint to this URL and subscribe to the{" "}
          <strong>checkout.session.completed</strong> event. Copy the endpoint&rsquo;s signing secret
          (<code>whsec_…</code>) into the field below so we can verify incoming events. Add one
          endpoint in test mode and one in live mode — each has its own signing secret.
        </p>
        <div className="form-field">
          <div className="page__subtitle--badges">
            <input className="input" value={webhookUrl} readOnly onFocus={(e) => e.target.select()} />
            <button type="button" className="btn btn--secondary btn--sm" onClick={() => void copyWebhookUrl()}>
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </div>
      </section>

      <form
        className="entry-form"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <section className="panel">
          <div className="panel__header">
            <h2 className="panel__title">Credentials</h2>
          </div>

          <div className="form-field">
            <label className="form-field__label" htmlFor="stripe-mode">
              These credentials are for
            </label>
            <select
              id="stripe-mode"
              className="input"
              value={mode}
              onChange={(e) => changeMode(e.target.value as StripeMode)}
            >
              <option value="live">Live</option>
              <option value="test">Test</option>
            </select>
            <span className="form-hint muted">
              Which mode this set belongs to — not which is active. The active one is chosen by the
              site-wide dev-mode toggle.
            </span>
          </div>

          <div className="form-field">
            <label className="form-field__label" htmlFor="stripe-secret-key">
              Secret key
            </label>
            <input
              id="stripe-secret-key"
              className="input"
              type="password"
              autoComplete="off"
              value={secretKey}
              onChange={(e) => setSecretKey(e.target.value)}
              placeholder={
                modeStatus?.hasSecretKey
                  ? "•••••••• (leave blank to keep)"
                  : mode === "live"
                    ? "sk_live_…"
                    : "sk_test_…"
              }
            />
          </div>

          <div className="form-field">
            <label className="form-field__label" htmlFor="stripe-signing-secret">
              Webhook signing secret
            </label>
            <input
              id="stripe-signing-secret"
              className="input"
              type="password"
              autoComplete="off"
              value={signingSecret}
              onChange={(e) => setSigningSecret(e.target.value)}
              placeholder={modeStatus?.hasWebhookSigningSecret ? "•••••••• (leave blank to keep)" : "whsec_…"}
            />
          </div>
        </section>

        <section className="panel">
          <div className="panel__header">
            <h2 className="panel__title">SKU metadata key</h2>
          </div>
          <div className="form-field">
            <input
              id="stripe-sku-key"
              className="input"
              value={skuMetadataKey}
              onChange={(e) => setSkuMetadataKey(e.target.value)}
              placeholder="sku"
            />
            <span className="form-hint muted">
              Shared across both modes. The Stripe Product metadata field whose value we match against
              our SKU (default &ldquo;sku&rdquo;).
            </span>
          </div>
        </section>

        <div className="form-actions form-actions--split">
          <div className="form-actions__group">
            <button type="submit" className="btn btn--primary" disabled={saving}>
              {saving ? "Saving…" : `Save ${MODE_LABEL[mode]} settings`}
            </button>
            {saved ? <span className="muted">Saved.</span> : null}
          </div>
        </div>
      </form>
    </div>
  );
}
